import { QuantitySelectorComponent } from '@theme/component-quantity-selector';

const LUMIERE_VARIANT_ID = '58713643090268';
const LUMIERE_ALLOWED_QUANTITIES = [1, 3, 4, 6];

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * For the commercial Lumière SKU, cart quantities are constrained to the approved sets 1 / 3 / 4 / 6.
 *
 * @extends {QuantitySelectorComponent}
 */
class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  connectedCallback() {
    super.connectedCallback();

    if (this.#isLumiere()) {
      const { quantityInput } = this.refs;
      quantityInput.readOnly = true;
      quantityInput.setAttribute('inputmode', 'none');
      quantityInput.setAttribute('aria-label', 'Quantità Lumière: scegli 1, 3, 4 o 6');
      this.#syncLumiereCheckoutGuard();
    }
  }

  /** @returns {boolean} */
  #isLumiere() {
    return this.dataset.variantId === LUMIERE_VARIANT_ID;
  }

  /**
   * Gets the effective maximum value for cart quantity selector.
   * Cart page: uses absolute max (how much can be in cart total).
   * @returns {number | null}
   */
  getEffectiveMax() {
    if (this.#isLumiere()) return 6;
    const { max } = this.getCurrentValues();
    return max;
  }

  /**
   * For Lumière, +/- moves only through the approved commercial sets.
   * @param {number} stepMultiplier
   */
  updateQuantity(stepMultiplier) {
    if (!this.#isLumiere()) {
      super.updateQuantity(stepMultiplier);
      return;
    }

    const { quantityInput } = this.refs;
    const current = Number(quantityInput.value) || 1;
    let next = current;

    if (stepMultiplier > 0) {
      next = LUMIERE_ALLOWED_QUANTITIES.find((quantity) => quantity > current) ?? 6;
    } else {
      next = [...LUMIERE_ALLOWED_QUANTITIES].reverse().find((quantity) => quantity < current) ?? 1;
    }

    quantityInput.value = String(next);
    this.onQuantityChange();
    this.updateButtonStates();
    this.#syncLumiereCheckoutGuard();
  }

  /**
   * Prevent free-form Lumière quantities if a browser or script changes the readonly input.
   * @param {Event} event
   */
  setQuantity(event) {
    if (!this.#isLumiere()) {
      super.setQuantity(event);
      return;
    }

    event.preventDefault();
    const { quantityInput } = this.refs;
    const requested = Number(quantityInput.value) || 1;
    const normalized = LUMIERE_ALLOWED_QUANTITIES.reduce((best, quantity) => {
      return Math.abs(quantity - requested) < Math.abs(best - requested) ? quantity : best;
    }, 1);

    quantityInput.value = String(normalized);
    this.onQuantityChange();
    this.updateButtonStates();
    this.#syncLumiereCheckoutGuard();
  }

  /**
   * Updates button states based on current value and limits.
   */
  updateButtonStates() {
    if (!this.#isLumiere()) {
      super.updateButtonStates();
      return;
    }

    const { minusButton, plusButton } = this.refs;
    const value = Number(this.refs.quantityInput.value) || 1;
    minusButton.disabled = value <= 1;
    plusButton.disabled = value >= 6;
  }

  /**
   * Re-check the guard after section morphs/cart updates.
   */
  updateCartQuantity() {
    if (!this.#isLumiere()) {
      super.updateCartQuantity();
      return;
    }

    this.updateButtonStates();
    this.#syncLumiereCheckoutGuard();
  }

  #syncLumiereCheckoutGuard() {
    queueMicrotask(() => {
      const selectors = document.querySelectorAll(`cart-quantity-selector-component[data-variant-id="${LUMIERE_VARIANT_ID}"]`);
      let hasInvalidLumiereQuantity = false;

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        const value = Number(input?.value || 0);
        if (!LUMIERE_ALLOWED_QUANTITIES.includes(value)) {
          hasInvalidLumiereQuantity = true;
          break;
        }
      }

      for (const checkout of document.querySelectorAll('#checkout, .cart__checkout-button')) {
        if (checkout instanceof HTMLButtonElement) checkout.disabled = hasInvalidLumiereQuantity;
      }

      for (const accelerated of document.querySelectorAll('.additional-checkout-buttons')) {
        if (accelerated instanceof HTMLElement) accelerated.hidden = hasInvalidLumiereQuantity;
      }

      const existingNotice = document.querySelector('[data-lumiere-cart-guard-notice]');
      if (hasInvalidLumiereQuantity) {
        if (!existingNotice) {
          const ctas = document.querySelector('.cart__ctas');
          if (ctas) {
            const notice = document.createElement('p');
            notice.dataset.lumiereCartGuardNotice = '';
            notice.setAttribute('role', 'alert');
            notice.style.margin = '0 0 10px';
            notice.style.fontSize = '12px';
            notice.textContent = 'Per Lumière scegli una configurazione disponibile: 1, 3, 4 o 6 lampade.';
            ctas.before(notice);
          }
        }
      } else {
        existingNotice?.remove();
      }
    });
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
